import type { MouseEvent } from 'react';
import React, { useMemo } from 'react';
import { withTheme } from 'styled-components';
import Modal from '@this/shared/modal/modal';
import { Link } from '@this/shared/ui/navigations/link';
import { Button } from '@this/shared/ui/inputs/button';
import { Checkbox } from '@this/shared/ui/inputs/checkbox';
import CancelPolicyContent from '@this/components/trips_management/cancel_policy_content';
import PremiumSupportBadge from '@this/components/trips_management/premium_support_badge';

const AITRAVEL_SERVICE_ID = 1;

interface Props {
  show: boolean;
  submitting: boolean;
  trip: any;
  service: { name: string; tel: string };
  serviceId?: number;
  handleHide: () => void;
  handleSubmit: (e?: MouseEvent<HTMLAnchorElement>) => void;
  theme: { themeClass: string };
}

const CancelTripModal: React.FC<Props> = ({
  show,
  submitting,
  trip,
  service,
  serviceId,
  handleHide,
  handleSubmit,
  children,
  theme
}) => {
  const isAiTravel = (serviceId ?? trip.serviceId) === AITRAVEL_SERVICE_ID;
  const isPremiumSupport = !!trip.premiumSupportActive;
  const [agreedToTerms, setAgreedToTerms] = React.useState(false);
  const [requestConfirmed, setRequestConfirmed] = React.useState(false);
  const styles = useMemo(() => getStyles(), []);

  return (
    <Modal
      show={show}
      hideModal={handleHide}
      maxWidth={1200}
      title="下記の旅程をキャンセル依頼する"
      titleRight={isAiTravel ? <PremiumSupportBadge isPremiumSupport={isPremiumSupport} /> : undefined}
    >
      {trip.status === 5 ? (
        <p>この旅程はキャンセル済みです</p>
      ) : trip.status === 6 ? (
        <p>
          この旅程はキャンセル対応中です。
          <br />
          対応が完了するまで少々 お待ち下さい。
        </p>
      ) : submitting ? (
        <p>処理中...</p>
      ) : (
        children && (
          <div style={styles.modalContentWrapper}>
            <div style={styles.modalContent}>
              {isAiTravel && <h1 style={styles.sectionTitle}>キャンセル依頼内容</h1>}
              {children}
              {theme.themeClass === 'tabikobo' && trip.foreign_air && (
                <p style={styles.cancelFee}>
                  {`キャンセル手数料：${trip.cancelFeeText()}`}
                  <br />
                  航空券発券後は、キャンセル手数料の他、キャンセル規程に従いキャンセル料が発生します。
                </p>
              )}
              {isAiTravel ? (
                <>
                  <div style={styles.consentArea}>
                    <h1 style={styles.sectionTitle}>以下の内容を必ずご確認ください</h1>
                    <Checkbox checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)}>
                      右の同意事項・注意事項に同意しました。
                    </Checkbox>
                    <Checkbox checked={requestConfirmed} onChange={e => setRequestConfirmed(e.target.checked)}>
                      キャンセル依頼内容を確認しました。
                    </Checkbox>
                  </div>
                  <div style={styles.buttonArea}>
                    <Button color="sub" onClick={handleHide}>
                      閉じる
                    </Button>
                    <Button
                      color="primary"
                      onClick={() => handleSubmit()}
                      disabled={!agreedToTerms || !requestConfirmed || !trip.cancelRequestable}
                    >
                      決定する
                    </Button>
                  </div>
                  {!trip.cancelRequestable && (
                    <div style={{ margin: '10px 0' }}>
                      <p>※ キャンセル依頼のリクエストは、ご利用前営業日18時まで可能です。</p>
                      <p>
                        ご利用当日のキャンセル依頼に関しましては、当画面の
                        <span style={{ fontWeight: 'bold' }}>【ご利用当日のキャンセル依頼】</span>
                        をご確認ください。
                      </p>
                    </div>
                  )}
                </>
              ) : null}
            </div>
            <div style={styles.cancelConfirmPolicy}>
              {isAiTravel ? (
                <CancelPolicyContent
                  tripId={trip.id}
                  serviceTel={service.tel}
                  isPremiumSupport={isPremiumSupport}
                  mode="bulk"
                />
              ) : (
                <>
                  <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>[ご利用当日のキャンセル依頼］</p>
                  <ul style={{ listStyle: 'disc', paddingLeft: '20px', marginBottom: '20px' }}>
                    <li>
                      当日のキャンセルは、ご自身でご対応をお願いいたします。
                      {theme.themeClass !== 'tabikobo' && (
                        <>
                          （キャンセル方法は
                          <Link isExternal href="https://aitravel.jp/knowledges/000001075">
                            こちら
                          </Link>
                          をご確認ください）
                        </>
                      )}
                    </li>
                    <li>
                      ご自身でキャンセルした場合は、弊社にてご請求データを更新する必要がございます。キャンセルがお済みになりましたら、その旨チャットよりお知らせください。
                    </li>
                  </ul>

                  <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>[キャンセル料金について]</p>
                  <ul style={{ listStyle: 'disc', paddingLeft: '20px', marginBottom: '20px' }}>
                    <li>キャンセルした場合、キャンセル規程に則りキャンセル料金が発生する場合がございます。</li>
                    <li>各提携先の規程により、返金できない場合がございます。</li>
                    <li>返金不可プラン等、キャンセル料金が100%発生している場合、返金はございません。</li>
                    <li>キャンセルご依頼後、キャンセルの取り消しはできかねます。</li>
                    <li>弊社事務手数料は、返金対象外でございます。</li>
                    <li>
                      旅程内の一部商品のみをキャンセルしたい場合、旅程詳細画面より各個別キャンセル依頼をご利用ください。
                    </li>
                  </ul>

                  <p style={{ marginBottom: '20px' }}>質問、不明点がある場合は下記までお問い合わせください。</p>
                  <table>
                    <tbody>
                      <tr>
                        <th>ツール</th>
                        <th>詳細</th>
                      </tr>
                      <tr>
                        <td>チャット</td>
                        <td>
                          <Link href={`/trips/${trip.id}`}>チャットへのリンク</Link>
                        </td>
                      </tr>
                      <tr>
                        <td>電話番号</td>
                        <td>{service.tel}</td>
                      </tr>
                      <tr>
                        <td>問い合わせフォーム</td>
                        <td>
                          <Link href="/inquiries/new">お問い合わせフォームへのリンク</Link>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <p style={{ marginBottom: '40px' }}>
                    ※平日18時以降、土日、祝日にご依頼いただいたキャンセル対応は翌営業日の対応になります。予めご了承くださいますようお願い申し上げます。
                  </p>
                  <div style={{ marginBottom: '20px' }}>
                    <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                      この予約を本当にキャンセル依頼しますか？
                    </p>
                    <div style={{ display: 'flex', justifyContent: 'end', gap: '20px' }}>
                      <Button color="sub" onClick={handleHide}>
                        いいえ
                      </Button>
                      <Button color="primary" onClick={() => handleSubmit()} disabled={!trip.cancelRequestable}>
                        はい
                      </Button>
                    </div>
                  </div>
                  {!trip.cancelRequestable && (
                    <div style={{ margin: '10px 0' }}>
                      <p>※ キャンセル依頼のリクエストは、ご利用前営業日18時まで可能です。</p>
                      <p>
                        ご利用当日のキャンセル依頼に関しましては、当画面の
                        <span style={{ fontWeight: 'bold' }}>[ご利用当日のキャンセル依頼］</span>
                        をご確認ください。
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )
      )}
    </Modal>
  );
};

const getStyles = () => ({
  cancelFee: {
    marginBottom: 10
  },
  modalContentWrapper: {
    display: 'flex' as const,
    flexWrap: 'wrap' as const,
    gap: 20
  },
  modalContent: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    flex: 6,
    minWidth: 0
  },
  cancelConfirmPolicy: {
    padding: 20,
    background: '#eee',
    flex: 4,
    minWidth: 0,
    width: 'auto'
  },
  consentArea: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
    gap: 10,
    margin: '40px 0 20px'
  },
  sectionTitle: {
    fontSize: 18,
    marginBottom: 10
  },
  buttonArea: {
    display: 'flex' as const,
    justifyContent: 'center' as const,
    gap: 20
  }
});

export default withTheme(CancelTripModal);
